<template>
  <div class="bg-gray-50">
    <div class="mx-auto max-w-6xl px-4 py-12 lg:px-8 lg:py-16">
      <div class="grid items-center gap-8 rounded-[32px] border border-orange-100 bg-white p-7 shadow-card md:grid-cols-[1.1fr_.9fr] md:p-10">
        <div>
        <p class="text-xs font-bold uppercase tracking-[0.2em] text-brand">Simple pricing</p>
        <h1 class="mt-3 text-3xl font-bold tracking-tight text-ink sm:text-4xl">Choose the plan that fits your events</h1>
        <p class="mt-3 max-w-xl text-base leading-7 text-muted">Start with the essentials, add collaboration as you grow, or choose a plan tailored to your organization.</p>
        <div class="mt-6 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-2 py-1 shadow-card">
          <button
            class="rounded-full px-4 py-2 text-sm font-semibold"
            :class="billingCycle === 'monthly' ? 'bg-brand text-white shadow-card' : 'text-ink'"
            @click="billingCycle = 'monthly'"
          >
            Monthly
          </button>
          <button
            class="rounded-full px-4 py-2 text-sm font-semibold"
            :class="billingCycle === 'yearly' ? 'bg-brand text-white shadow-card' : 'text-ink'"
            @click="billingCycle = 'yearly'"
          >
            Yearly <span class="text-[11px] font-semibold text-white/80" v-if="billingCycle === 'yearly'">(save 15%)</span>
          </button>
        </div></div>
        <img src="/assets/illustrations/pricing-page.svg" alt="A person comparing pricing options" class="mx-auto h-60 w-full max-w-sm object-contain" />
      </div>

      <div class="mt-8 grid gap-6 lg:grid-cols-4">
        <div
          v-for="plan in plans"
          :key="plan.id"
          class="relative flex flex-col rounded-3xl bg-white p-5 shadow-card ring-1 transition hover:-translate-y-1 hover:shadow-xl"
          :class="plan.recommended ? 'ring-2 ring-brand' : 'ring-gray-100'"
        >
          <span v-if="plan.recommended" class="absolute -top-3 right-5 rounded-full bg-brand px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">Most popular</span>
          <p class="text-sm font-semibold text-ink">{{ plan.title }}</p>
          <p class="mt-1 text-xs uppercase tracking-wide text-muted">{{ plan.tag }}</p>
          <p class="mt-4 text-3xl font-semibold text-ink">
            {{ priceFor(plan) }}
            <span class="text-sm font-normal text-muted">/{{ billingCycle === 'monthly' ? 'mo' : 'yr' }}</span>
          </p>
          <p class="mt-1 text-sm text-muted">{{ plan.copy }}</p>
          <div class="mt-4 space-y-2 text-sm text-ink">
            <div v-for="(feat, idx) in plan.features" :key="idx" class="flex items-center gap-2">
              <i class="ri-check-line text-brand"></i>
              <span class="text-muted">{{ feat }}</span>
            </div>
          </div>
          <button
            class="mt-auto rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white shadow-card transition hover:-translate-y-0.5 hover:bg-brandDark"
            type="button"
          >
            {{ plan.cta }}
          </button>
        </div>
      </div>

      <div class="mt-12 rounded-3xl bg-white p-6 shadow-card ring-1 ring-gray-100">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p class="text-xs font-semibold uppercase text-muted">Full feature comparison</p>
            <h2 class="text-xl font-semibold text-ink">Know exactly what’s included</h2>
          </div>
          <button class="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand">Download PDF</button>
        </div>
        <div class="mt-4 overflow-x-auto">
          <table class="w-full text-left text-sm text-ink">
            <thead>
              <tr class="border-b border-gray-100">
                <th class="py-3 pr-4 font-semibold text-muted">Feature</th>
                <th v-for="plan in plans" :key="plan.id" class="py-3 pr-4 font-semibold text-muted">{{ plan.title }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in comparison" :key="row.label" class="border-b border-gray-50">
                <td class="py-3 pr-4 text-muted">{{ row.label }}</td>
                <td v-for="plan in plans" :key="plan.id" class="py-3 pr-4">
                  <span v-if="row.values[plan.id] === true" class="text-brand"><i class="ri-check-line"></i></span>
                  <span v-else-if="row.values[plan.id] === false" class="text-gray-300"><i class="ri-close-line"></i></span>
                  <span v-else class="text-ink">{{ row.values[plan.id] }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="mt-10 rounded-3xl bg-gradient-to-r from-brand/10 via-white to-indigo-50 p-6 ring-1 ring-brand/20">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p class="text-sm font-semibold text-ink">Supercharge your plan</p>
            <p class="text-sm text-muted">Add concierge, white-label invites, or advanced analytics anytime.</p>
          </div>
          <div class="flex gap-2">
            <button class="rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white shadow-card transition hover:-translate-y-0.5 hover:bg-brandDark">Talk to sales</button>
            <button class="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand">Explore add-ons</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const billingCycle = ref('monthly')

const plans = [
  {
    id: 'basic',
    title: 'Basic',
    tag: 'For simple events',
    monthly: '$19',
    yearly: '$190',
    copy: 'Launch clean invites and track RSVPs for smaller gatherings.',
    features: ['Unlimited invites', 'RSVP tracking', 'Basic vendor search'],
    cta: 'Start basic'
  },
  {
    id: 'pro',
    title: 'Pro',
    recommended: true,
    tag: 'Best for teams',
    monthly: '$49',
    yearly: '$490',
    copy: 'Concierge vendor holds, co-hosts, and branded invites.',
    features: ['Co-host roles', 'Vendor holds', 'Custom branding'],
    cta: 'Upgrade to Pro'
  },
  {
    id: 'ppu',
    title: 'Pay per use',
    tag: 'Only pay when you launch',
    monthly: '$0',
    yearly: '$0',
    copy: 'Pay only when you publish invites or place vendor holds.',
    features: ['$5 per published invite', '$15 per vendor hold', 'Access to all templates'],
    cta: 'Use on-demand'
  },
  {
    id: 'enterprise',
    title: 'Enterprise',
    tag: 'For complex orgs',
    monthly: 'Custom',
    yearly: 'Custom',
    copy: 'SSO, approvals, procurement workflows, and white-label experiences.',
    features: ['SSO + SCIM', 'Advanced analytics', 'Dedicated manager'],
    cta: 'Contact sales'
  }
]

const comparison = [
  {
    label: 'Custom branding',
    values: { basic: false, pro: true, ppu: true, enterprise: true }
  },
  {
    label: 'Co-host roles',
    values: { basic: false, pro: true, ppu: true, enterprise: true }
  },
  {
    label: 'Vendor holds',
    values: { basic: false, pro: true, ppu: 'Per hold', enterprise: 'Unlimited' }
  },
  {
    label: 'Concierge support',
    values: { basic: false, pro: 'Standard', ppu: 'Standard', enterprise: 'Priority' }
  },
  {
    label: 'Analytics',
    values: { basic: 'Summary', pro: 'Detailed', ppu: 'Detailed', enterprise: 'Org-wide' }
  },
  {
    label: 'SSO / SCIM',
    values: { basic: false, pro: false, ppu: false, enterprise: true }
  }
]

const priceFor = (plan) => (billingCycle.value === 'monthly' ? plan.monthly : plan.yearly)
</script>
